// Hook WebSocket avec auto-reconnect + abonnements
import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";

const WS_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || "";
  return url.replace(/^http/, "ws") + "/api/ws";
})();

export function useWebSocket() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const send = useCallback((payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("rops_token");
    if (!token) return;

    let alive = true;

    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!alive) return;
          setConnected(true);
          // Heartbeat every 25s
          ws._hb = setInterval(() => {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
          }, 25000);
        };

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            listenersRef.current.forEach((fn) => fn(msg));
          } catch {}
        };

        ws.onclose = () => {
          if (ws._hb) clearInterval(ws._hb);
          setConnected(false);
          if (alive) {
            reconnectTimerRef.current = setTimeout(connect, 3000);
          }
        };

        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        if (alive) reconnectTimerRef.current = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      alive = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        if (wsRef.current._hb) clearInterval(wsRef.current._hb);
        try { wsRef.current.close(); } catch {}
      }
    };
  }, [user]);

  return { connected, subscribe, send };
}
